import { useState } from 'react';
import { trpc, trpcUntypedClient } from '../lib/api';
import {
  Button,
  Card,
  Row,
  Col,
  Typography,
  DatePicker,
  Select,
  Space,
  Spin,
  Statistic,
  Alert,
  Tag,
  message,
} from 'antd';
import { ResponsiveTable, type ResponsiveColumnType } from '../components/common/responsive';
import { Column, Pie } from '@ant-design/charts';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  BarChartOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { resolveCategoryDisplay, type CategoryLike } from '../utils/categoryDisplay';
import { PageHeader, PageContainer } from '../components/common';
import {
  buildCostPieLabelConfig,
  formatAnalyticsCurrency as formatCurrency,
  type CurrencyLike,
} from './partsAnalyticsChartConfig';

type CostTableRecord = {
  partId: string;
  partName: string;
  partDisplayId?: string;
  category?: CategoryLike;
  totalQuantity: number;
  unitPrice?: CurrencyLike;
  totalCost: CurrencyLike;
};

type BudgetTableRecord = {
  partId: string;
  partName: string;
  category?: CategoryLike;
  averageMonthlyConsumption: number;
  predictedQuantity: number;
  unitPrice: CurrencyLike;
  estimatedCost: CurrencyLike;
};

type CategoryConsumptionReportResult = {
  filename: string;
  contentBase64: string;
  mimeType: string;
};

const { Text } = Typography;
const { RangePicker } = DatePicker;

const downloadBase64File = (filename: string, contentBase64: string, mimeType: string) => {
  const binaryString = window.atob(contentBase64);
  const bytes = new Uint8Array(binaryString.length);
  for (let index = 0; index < binaryString.length; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }
  const blob = new Blob([bytes], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export function PartsAnalyticsPage() {
  const [messageApi, contextHolder] = message.useMessage();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(6, 'month'),
    dayjs(),
  ]);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [selectedMonth, setSelectedMonth] = useState<number | undefined>();
  const [selectedPartCategoryId, setSelectedPartCategoryId] = useState<string | undefined>();
  const [isExportingCategoryReport, setIsExportingCategoryReport] = useState(false);

  // 消費トレンド取得
  const { data: trendData, isLoading: trendLoading } = trpc.partsAnalytics.getConsumptionTrend.useQuery({
    startDate: dateRange[0].toDate(),
    endDate: dateRange[1].toDate(),
  });

  // コスト分析取得
  const { data: costData, isLoading: costLoading } = trpc.partsAnalytics.getPartCostAnalysis.useQuery({
    startDate: dateRange[0].toDate(),
    endDate: dateRange[1].toDate(),
  });

  // 月次予算計画取得
  const { data: budgetData, isLoading: budgetLoading } = trpc.partsAnalytics.getMonthlyBudgetPlan.useQuery({
    year: selectedYear,
    month: selectedMonth,
  });

  // 発注履歴分析取得
  const { data: purchaseHistory, isLoading: purchaseLoading } = trpc.partsAnalytics.getPurchaseHistory.useQuery({
    months: 12,
  });

  const { data: partCategories = [] } = trpc.partCategory.list.useQuery({
    includeInactive: false,
    sortBy: 'display_order',
    sortOrder: 'asc',
  });

  // 消費トレンドグラフデータ
  const consumptionChartData = trendData?.map(item => ({
    month: item.month,
    部品・消耗品名: item.partName,
    消費数: item.totalQuantity,
  })) || [];

  // コスト円グラフデータ
  const categoryTotals = (costData?.categoryTotals ?? {}) as Record<string, { totalCost: number }>;
  const costPieData = Object.entries(categoryTotals).map(([category, data]) => ({
    type: category,
    value: data.totalCost,
  }));

  // 発注履歴グラフデータ
  const purchaseChartData = purchaseHistory?.monthlyData.map(item => ({
    month: item.month,
    発注回数: item.orderCount,
    発注金額: parseFloat(item.totalAmount.toString()),
  })) || [];

  const costTableData = (costData?.parts ?? []) as CostTableRecord[];

  const renderCategoryCell = (category: CategoryLike) => {
    const display = resolveCategoryDisplay(category);
    if (!display) {
      return '-';
    }
    return <Tag color={display.color}>{display.label}</Tag>;
  };

  // コストテーブルカラム
  const costColumns: ResponsiveColumnType<CostTableRecord>[] = [
    {
      title: '部品・消耗品名',
      dataIndex: 'partName',
      key: 'partName',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.partDisplayId}</Text>
        </Space>
      ),
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: 'カテゴリ',
      dataIndex: 'category',
      key: 'category',
      render: (cat) => renderCategoryCell(cat),
      mobileRole: 'tag',
      mobilePriority: 2,
    },
    {
      title: '消費数',
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      align: 'right',
      mobileRole: 'description',
      mobilePriority: 3,
    },
    {
      title: '単価(税込)',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      align: 'right',
      render: (price) => formatCurrency(price),
      mobileRole: 'description',
      mobilePriority: 4,
    },
    {
      title: '合計コスト',
      dataIndex: 'totalCost',
      key: 'totalCost',
      align: 'right',
      render: (cost) => formatCurrency(cost),
      mobileRole: 'tag',
      mobilePriority: 3,
    },
  ];

  // 予算計画テーブルカラム
  const budgetColumns: ResponsiveColumnType<BudgetTableRecord>[] = [
    {
      title: '部品・消耗品名',
      dataIndex: 'partName',
      key: 'partName',
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: 'カテゴリ',
      dataIndex: 'category',
      key: 'category',
      render: (cat) => renderCategoryCell(cat),
      mobileRole: 'tag',
      mobilePriority: 2,
    },
    {
      title: '月平均消費',
      dataIndex: 'averageMonthlyConsumption',
      key: 'averageMonthlyConsumption',
      align: 'right',
      mobileRole: 'description',
      mobilePriority: 3,
    },
    {
      title: '予測消費数',
      dataIndex: 'predictedQuantity',
      key: 'predictedQuantity',
      align: 'right',
      mobileRole: 'description',
      mobilePriority: 4,
    },
    {
      title: '単価(税込)',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      align: 'right',
      render: (price) => formatCurrency(price),
      mobileRole: 'description',
      mobilePriority: 5,
    },
    {
      title: '予測コスト',
      dataIndex: 'estimatedCost',
      key: 'estimatedCost',
      align: 'right',
      render: (cost) => formatCurrency(cost),
      mobileRole: 'tag',
      mobilePriority: 3,
    },
  ];

  const budgetTableData = (budgetData?.parts ?? []) as BudgetTableRecord[];

  const isLoading = trendLoading || costLoading || budgetLoading || purchaseLoading;

  const partCategoryOptions = partCategories.map((category) => ({
    value: category.id,
    label: category.categoryLabel,
  }));

  const handleExportCategoryConsumptionReport = async () => {
    if (isExportingCategoryReport) return;

    setIsExportingCategoryReport(true);
    try {
      const result = await trpcUntypedClient.mutation('partsAnalytics.exportCategoryConsumptionReport', {
        startDate: dateRange[0].startOf('day').toDate(),
        endDate: dateRange[1].endOf('day').toDate(),
        partCategoryId: selectedPartCategoryId,
      }) as CategoryConsumptionReportResult;
      downloadBase64File(result.filename, result.contentBase64, result.mimeType);
      messageApi.success('カテゴリ別消費Excelを出力しました');
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'カテゴリ別消費Excelの出力に失敗しました';
      messageApi.error(messageText);
    } finally {
      setIsExportingCategoryReport(false);
    }
  };

  return (
    <PageContainer>
      {contextHolder}
      <PageHeader
        title="部品・消耗品分析ダッシュボード"
        subtitle="消費トレンド、コスト分析、予算計画を確認できます"
      />

      {/* 期間選択 */}
      <Card style={{ marginBottom: 16 }}>
        <Space size="large" wrap>
          <div>
            <Text strong style={{ marginRight: 8 }}>分析期間:</Text>
            <RangePicker
              value={dateRange}
              onChange={(dates) => dates && setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
              format="YYYY-MM-DD"
            />
          </div>
          <div>
            <Text strong style={{ marginRight: 8 }}>予算計画:</Text>
            <Select
              value={selectedYear}
              onChange={setSelectedYear}
              style={{ width: 100, marginRight: 8 }}
            >
              {[2024, 2025, 2026].map(y => (
                <Select.Option key={y} value={y}>{y}年</Select.Option>
              ))}
            </Select>
            <Select
              value={selectedMonth}
              onChange={setSelectedMonth}
              style={{ width: 100 }}
              placeholder="全体"
              allowClear
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <Select.Option key={m} value={m}>{m}月</Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <Text strong style={{ marginRight: 8 }}>部品カテゴリ:</Text>
            <Select
              value={selectedPartCategoryId}
              onChange={setSelectedPartCategoryId}
              style={{ width: 220 }}
              placeholder="すべてのカテゴリ"
              allowClear
              options={partCategoryOptions}
            />
          </div>
          <Button
            icon={<DownloadOutlined aria-hidden="true" />}
            loading={isExportingCategoryReport}
            onClick={handleExportCategoryConsumptionReport}
          >
            カテゴリ別消費Excelを出力
          </Button>
        </Space>
      </Card>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* サマリー統計 */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="総消費コスト"
                  value={costData?.totalCost || 0}
                  precision={0}
                  suffix="円"
                  prefix={<DollarOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="月次予算（予測）"
                  value={budgetData?.totalBudget || 0}
                  precision={0}
                  suffix="円"
                  prefix={<BarChartOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="発注総額"
                  value={purchaseHistory?.totalSpent || 0}
                  precision={0}
                  suffix="円"
                  prefix={<ShoppingCartOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="発注回数"
                  value={purchaseHistory?.totalOrders || 0}
                  suffix="回"
                  prefix={<ShoppingCartOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* 消費トレンドグラフ */}
          <Card title="月次消費トレンド" style={{ marginBottom: 16 }}>
            {consumptionChartData.length > 0 ? (
              <Column
                data={consumptionChartData}
                xField="month"
                yField="消費数"
                seriesField="部品・消耗品名"
                isGroup={true}
                columnStyle={{
                  radius: [4, 4, 0, 0],
                }}
                height={300}
              />
            ) : (
              <Alert message="消費データがありません" type="info" />
            )}
          </Card>

          {/* コスト分析 */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Card title="カテゴリ別コスト分布">
                {costPieData.length > 0 ? (
                  <Pie
                    data={costPieData}
                    angleField="value"
                    colorField="type"
                    radius={0.8}
                    label={buildCostPieLabelConfig()}
                    height={300}
                  />
                ) : (
                  <Alert message="コストデータがありません" type="info" />
                )}
              </Card>
            </Col>
            <Col span={12}>
              <Card title="発注履歴推移">
                {purchaseChartData.length > 0 ? (
                  <Column
                    data={purchaseChartData}
                    xField="month"
                    yField="発注金額"
                    meta={{
                      発注金額: {
                        alias: '発注金額',
                        formatter: (v: number) => `¥${v.toLocaleString()}`,
                      },
                    }}
                    height={300}
                  />
                ) : (
                  <Alert message="発注履歴がありません" type="info" />
                )}
              </Card>
            </Col>
          </Row>

          {/* 部品・消耗品別コスト詳細 */}
          <Card title="部品・消耗品別コスト詳細" style={{ marginBottom: 16 }}>
            <ResponsiveTable
              columns={costColumns}
              dataSource={costTableData}
              rowKey="partId"
              pagination={{ pageSize: 10 }}
              mobileCardConfig={{
                titleField: 'partName',
              }}
            />
          </Card>

          {/* 月次予算計画 */}
          <Card title={`予算計画 - ${selectedYear}年${selectedMonth ? selectedMonth + '月' : ''}`}>
            <Alert
              message="予測に基づく予算計画"
              description="過去の消費データから算出された予測値です。実際の発注量は在庫状況を確認してください。"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <ResponsiveTable
              columns={budgetColumns}
              dataSource={budgetTableData}
              rowKey="partId"
              pagination={{ pageSize: 10 }}
              mobileCardConfig={{
                titleField: 'partName',
              }}
              summary={(pageData) => {
                const total = pageData.reduce((sum, item) => {
                  const value = typeof item.estimatedCost === 'number'
                    ? item.estimatedCost
                    : Number(item.estimatedCost);
                  return sum + (Number.isNaN(value) ? 0 : value);
                }, 0);
                return (
                  <>
                    <tr>
                      <th colSpan={5}>
                        <Text strong>合計予算</Text>
                      </th>
                      <td style={{ textAlign: 'right' }}>
                        <Text strong style={{ fontSize: 16 }}>
                          ¥{total.toLocaleString()}
                        </Text>
                      </td>
                    </tr>
                  </>
                );
              }}
            />
          </Card>
        </>
      )}
    </PageContainer>
  );
}
